import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { MacroregionName } from '~/config/cities';
import type { ComputedRef, Ref } from 'vue';

import { useTwoFieldSearchSuggestState } from '~/composables/useTwoFieldSearchSuggestState';
import { MACROREGIONS } from '~/config/cities';
import { computed, watch } from 'vue';

export interface UseTwoFieldSuggestReturn {
	firstFieldSelection: Ref<SelectAddressEvent | null>;
	firstFieldSearchTerm: Ref<string>;
	secondFieldSearchTerm: Ref<string>;
	selectedMacroregion: Ref<MacroregionName | (string & {}) | undefined>;
	// Computed state (derived)
	isSecondFieldEnabled: ComputedRef<boolean>;
	selectedBoundingRegion: ComputedRef<SelectAddressEvent | null>;

	// Methods
	clearFirstField: () => void;
	clearSecondField: () => void;
	clearAll: () => void;
}

/**
 * Composable to manage shared state between two search suggest fields.
 *
 * Features:
 * - Manages first and second field selections and search terms
 * - Auto-detects macroregion from first field selection
 * - Enables second field only when first field has a selection
 * - Provides cascade clearing (changing first field clears second field)
 *
 * The macroregion is automatically inferred from the first field selection:
 * - When user selects a city/macroregion, selectedMacroregion is updated
 * - This macroregion is used to scope the second field suggestions
 *
 * Note: For single-field search with filter sync, use useSingleFieldSuggest instead.
 *
 * @example
 * // Usage in two-field search components
 * const {
 *   firstFieldSelection,
 *   secondFieldSearchTerm,
 *   isSecondFieldEnabled,
 *   selectedMacroregion
 * } = useTwoFieldSuggest();
 */
export function useTwoFieldSuggest(): UseTwoFieldSuggestReturn {
	// Use shared state from useSearchSuggestState
	const {
		firstFieldSelection,
		firstFieldSearchTerm,
		secondFieldSearchTerm,
		selectedMacroregion
	} = useTwoFieldSearchSuggestState();

	/**
	 * Updates selectedMacroregion when user interacts with the search fields.
	 * Uses macroregion from selection or defaults to São Paulo.
	 */
	watch(
		firstFieldSelection,
		newSelection => {
			if (!newSelection) {
				selectedMacroregion.value = undefined;
				return;
			}

			// If user selected a macroregion/city directly
			if (['macroregion', 'city'].includes(newSelection.searchType ?? '')) {
				const newMacroregion =
					newSelection.macroregions?.[0] || MACROREGIONS.SAO_PAULO;
				if (newMacroregion !== selectedMacroregion.value) {
					selectedMacroregion.value = newMacroregion;
				}
				return;
			}

			if (
				['point_of_interest', 'street'].includes(newSelection.searchType || '')
			) {
				const newMacroregion =
					newSelection.macroregionUsed || MACROREGIONS.SAO_PAULO;

				if (newMacroregion !== selectedMacroregion.value) {
					selectedMacroregion.value = newMacroregion;
				}
			}
		},
		{ deep: true }
	);

	// Estado derivado - segundo campo está habilitado se primeiro tem valor
	const isSecondFieldEnabled = computed(() => !!firstFieldSelection.value);

	// Região selecionada para filtrar o segundo campo (bounding box)
	const selectedBoundingRegion = computed(() => firstFieldSelection.value);

	/**
	 * Limpa o primeiro campo
	 * Segundo campo limpa automaticamente via estado derivado
	 */
	function clearFirstField() {
		firstFieldSelection.value = null;
		firstFieldSearchTerm.value = '';
		secondFieldSearchTerm.value = '';
		selectedMacroregion.value = undefined;
	}

	/**
	 * Limpa o segundo campo
	 */
	function clearSecondField() {
		secondFieldSearchTerm.value = '';
	}

	/**
	 * Limpa todos os campos
	 */
	function clearAll() {
		clearFirstField();
		clearSecondField();
	}

	/**
	 * Cascade clearing: when first field changes or clears, second field is auto-cleared.
	 */
	watch(firstFieldSelection, (newValue, oldValue) => {
		const shouldClearSecondField =
			!newValue || (oldValue && newValue !== oldValue);

		if (shouldClearSecondField) {
			clearSecondField();
		}
	});

	return {
		// Shared state
		firstFieldSelection,
		firstFieldSearchTerm,
		secondFieldSearchTerm,
		selectedMacroregion,

		// Derived state
		isSecondFieldEnabled,
		selectedBoundingRegion,

		// Methods
		clearFirstField,
		clearSecondField,
		clearAll
	};
}
